// Durable client-side store for screening answer videos that have been recorded
// but not yet confirmed-uploaded. Without this, a recorded blob lives only in React
// state and is lost if the candidate refreshes or the tab reloads mid-upload — forcing
// a re-record. Persisting to IndexedDB lets us recover the exact blob after a reload and
// resume the upload, which matters most on slow/flaky connections where uploads are slow
// enough that a refresh during upload is a real risk.

const DB_NAME = "sequence-screening";
const STORE = "pending-uploads";
const DB_VERSION = 1;

export type PendingUpload = {
  token: string;
  questionId: number;
  qIndex: number;
  total: number;
  duration: number;
  switches: number;
  blob: Blob;
  savedAt: number;
};

function keyFor(token: string, questionId: number) {
  return `${token}__${questionId}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      reject(e);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// All operations swallow errors and degrade gracefully: if IndexedDB is unavailable
// (private mode, quota, old browser), the screening flow still works — it just loses
// the refresh-recovery safety net rather than breaking.
export async function savePending(rec: PendingUpload): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put({ key: keyFor(rec.token, rec.questionId), ...rec });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* best-effort */
  }
}

export async function deletePending(token: string, questionId: number): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(keyFor(token, questionId));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* best-effort */
  }
}

// Return the oldest pending upload for this token (the one to recover first), or null.
export async function getOldestPending(token: string): Promise<PendingUpload | null> {
  try {
    const db = await openDb();
    const all = await new Promise<any[]>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    db.close();
    const mine = all
      .filter((r) => r.token === token && r.blob)
      .sort((a, b) => a.savedAt - b.savedAt);
    return mine.length ? (mine[0] as PendingUpload) : null;
  } catch {
    return null;
  }
}

const TTL_MS = 24 * 60 * 60 * 1000;

// Delete entries older than 24 hours to prevent IndexedDB bloat from abandoned sessions.
export async function cleanupOldPending(): Promise<void> {
  try {
    const db = await openDb();
    const all = await new Promise<any[]>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    const now = Date.now();
    const staleKeys = all.filter((r) => now - (r.savedAt || 0) > TTL_MS).map((r) => r.key);
    if (staleKeys.length) {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite");
        const store = tx.objectStore(STORE);
        staleKeys.forEach((k) => store.delete(k));
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    }
    db.close();
  } catch {
    /* best-effort */
  }
}

// Clear every pending blob for a token — call once the session is fully submitted so we
// don't leave stale videos in the browser's storage.
export async function clearToken(token: string): Promise<void> {
  try {
    const db = await openDb();
    const all = await new Promise<any[]>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    const keys = all.filter((r) => r.token === token).map((r) => r.key);
    if (keys.length) {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite");
        const store = tx.objectStore(STORE);
        keys.forEach((k) => store.delete(k));
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    }
    db.close();
  } catch {
    /* best-effort */
  }
}
